/* eslint-disable no-console -- Existing API diagnostics are intentionally retained. */
import axios from 'axios';

// Determine environment based on hostname
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Use correct URL based on environment
const BASE_URL = isDevelopment
  ? 'http://localhost:8081' // Development URL
  : 'https://erp.escooterlab.ch/actor-management'; // Production URL

// API_URL is the base path for API calls
const API_URL = `${BASE_URL}/api/v1`;

// Profile-picture URLs are stored absolute in the DB, built server-side from
// ACTOR_SERVICE_URL. When that env is missing the backend baked in
// "http://localhost:8081", which is unreachable from the browser and blocked
// as mixed content on HTTPS. Rewrite any localhost host to the real base so
// existing rows and any future misconfig still render.
export const normalizeActorImageUrl = (url) => {
  if (!url || typeof url !== 'string') return url;
  return url.replace(/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i, BASE_URL);
};

const fixActorPicture = (actor) => {
  if (actor && actor.profile_picture_url) {
    actor.profile_picture_url = normalizeActorImageUrl(actor.profile_picture_url);
  }
  return actor;
};

// Log environment and URL for debugging
console.log(`Actor API running in ${isDevelopment ? 'DEVELOPMENT' : 'PRODUCTION'} mode`);
console.log(`Actor API base URL: ${API_URL}`);

// Protected Customer Identity: always send the JWT on actor requests. The
// backend masks individuals' contact data for non-privileged tokens; without
// a token the ERP UI would be treated like an internal service call.
axios.interceptors.request.use((config) => {
  const url = config.url || '';
  if (url.startsWith(BASE_URL) && !config.headers?.Authorization) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Cache utilities
const inFlightCache = new Map();
const resultCache = new Map();
// Negative cache — avoid re-fetching IDs that are known to fail (404 = orphaned,
// 5xx = backend currently broken). Without this, opening a list of 60+ processes
// re-issues a 404/500 per missing owner on every refresh, hanging the UI.
const errorCache = new Map();
// Long TTL for permanent-missing (404). Short TTL for server errors (so they retry).
const ERROR_CACHE_TTL_404 = 5 * 60 * 1000;  // 5 minutes
const ERROR_CACHE_TTL_5XX = 15 * 1000;       // 15 seconds

// ============================================================
// Circuit Breaker — prevents actor service slowness from
// blocking the entire project. When actor service fails
// repeatedly, requests return immediately with fallback data.
// ============================================================
const circuitBreaker = {
  failures: 0,
  lastFailureTime: null,
  state: 'CLOSED',       // CLOSED = normal, OPEN = failing fast, HALF_OPEN = testing
  FAILURE_THRESHOLD: 3,  // Open circuit after 3 consecutive failures
  COOLDOWN_MS: 30000,    // Try again after 30 seconds

  recordSuccess() {
    this.failures = 0;
    if (this.state !== 'CLOSED') {
      console.log('[Circuit Breaker] Actor service recovered. Circuit CLOSED.');
      this.state = 'CLOSED';
    }
  },

  recordFailure() {
    this.failures += 1;
    this.lastFailureTime = Date.now();
    if (this.failures >= this.FAILURE_THRESHOLD) {
      this.state = 'OPEN';
      console.warn(`[Circuit Breaker] Actor service failed ${this.failures} times. Circuit OPEN for ${this.COOLDOWN_MS / 1000}s.`);
    }
  },

  isOpen() {
    if (this.state === 'OPEN') {
      const elapsed = Date.now() - this.lastFailureTime;
      if (elapsed > this.COOLDOWN_MS) {
        this.state = 'HALF_OPEN';
        console.log('[Circuit Breaker] Cooldown elapsed. Circuit HALF_OPEN - testing actor service...');
        return false;
      }
      return true;
    }
    return false;
  }
};

const normalizeArgs = (args) => {
  return JSON.stringify(
    args.map((a) => {
      if (typeof a === 'object' && a !== null) {
        return Object.fromEntries(Object.entries(a).sort());
      }
      return a;
    })
  );
};

const withTimeout = (promise, ms = 20000) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Request timed out')), ms)),
  ]);

const withCache = (fn, { ttl = 1000, id = fn.name } = {}) => {
  const wrapper = async (...args) => {
    // Extract signal if it's the last argument
    const signal = args[args.length - 1] instanceof AbortSignal ? args.pop() : null;
    
    const cacheKey = `${id}:${normalizeArgs(args)}`;
    const now = Date.now();

    const cached = resultCache.get(cacheKey);
    if (cached && now - cached.timestamp < ttl) {
      return cached.data;
    }

    // Negative cache — re-throw the previously seen error without hitting the
    // network. This is what prevents a list of 60+ orphaned actor IDs from
    // re-firing 60 HTTP 404/500 requests on every list refresh.
    const cachedErr = errorCache.get(cacheKey);
    if (cachedErr && now - cachedErr.timestamp < cachedErr.ttl) {
      throw cachedErr.error;
    }

    if (inFlightCache.has(cacheKey)) {
      return inFlightCache.get(cacheKey);
    }

    // Circuit breaker: return fallback immediately if actor service is down
    if (circuitBreaker.isOpen()) {
      console.warn(`[Circuit Breaker] Actor service OPEN — returning fallback for: ${id}`);
      if (id === 'getActors') return { actors: [], total: 0, total_pages: 1, current_page: 1 };
      if (id === 'getActor') return null;
      throw new Error('Actor service unavailable (circuit open)');
    }

    const promise = (async () => {
      try {
        // Pass signal back to the function
        const data = await withTimeout(fn(...args, signal), 20000);
        circuitBreaker.recordSuccess();
        resultCache.set(cacheKey, { data, timestamp: Date.now() });
        // Clear any prior negative cache entry on success
        errorCache.delete(cacheKey);
        return data;
      } catch (error) {
        // Don't count cancelled requests as failures and don't cache them
        if (error.name === 'CanceledError' || error.name === 'AbortError') {
          throw error;
        }
        circuitBreaker.recordFailure();
        // Negative cache: 404s persist (orphaned IDs don't reappear),
        // 5xx errors expire fast so genuine recovery is picked up.
        const status = error?.response?.status;
        if (status === 404) {
          errorCache.set(cacheKey, { error, timestamp: Date.now(), ttl: ERROR_CACHE_TTL_404 });
        } else if (status >= 500 && status < 600) {
          errorCache.set(cacheKey, { error, timestamp: Date.now(), ttl: ERROR_CACHE_TTL_5XX });
        }
        throw error;
      } finally {
        inFlightCache.delete(cacheKey);
      }
    })();

    inFlightCache.set(cacheKey, promise);
    return promise;
  };

  wrapper.clearCache = () => {
    for (const key of resultCache.keys()) {
      if (key.startsWith(id + ':')) {
        resultCache.delete(key);
        inFlightCache.delete(key);
      }
    }
    for (const key of errorCache.keys()) {
      if (key.startsWith(id + ':')) {
        errorCache.delete(key);
      }
    }
  };

  return wrapper;
};

// ===================
// Raw methods
// ===================

const getActorsRaw = async (page = 1, limit = 10, filters = {}, signal = null) => {
  // Ensure filters is an object to prevent destructuring errors
  const safeFilters = filters || {};
  const {
    search, type, actor_ids, sort_by, sort_direction, is_staff, archived,
    award_id, certificate_level,
  } = safeFilters;
  const params = {
    page,
    limit,
    ...(search && { search }),
    ...(type && type !== 'all' && { type }),
    ...(actor_ids && { actor_ids }),
    ...(typeof is_staff === 'boolean' && { is_staff }),
    ...(archived && { archived }),
    ...(sort_by && { sort_by }),
    ...(sort_direction && { sort_direction }),
    // Recognition filters, answered by the server so they search every actor
    // and not only the page on screen.
    ...(award_id && award_id !== 'all' && { award_id }),
    ...(certificate_level && certificate_level !== 'all' && { certificate_level }),
  };

  console.log('Request params to actor API:', params);

  const response = await axios.get(`${API_URL}/actors`, { 
    params,
    signal, // Add abort signal
    timeout: 30000 // 30 second timeout
  });
  return {
    actors: (response.data.actors || []).map(fixActorPicture),
    total_pages: response.data.total_pages || 1,
    total: response.data.total || 0,
    page: response.data.page || response.data.current_page || page,
    current_page: response.data.current_page || response.data.page || page,
  };
};

// CRM reconciliation must propagate failures rather than interpreting a cached
// empty fallback as a verified complete actor index. Enterprise contacts only.
export const getSalesCrmEnterprisePage = (page, limit) => getActorsRaw(page, limit, {type:'enterprise'});

const getActorRaw = async (id) => {
  const response = await axios.get(`${API_URL}/actors/${id}`);
  return fixActorPicture(response.data);
};

const getActorHistory = async (actorId, params = {}) => {
  const response = await axios.get(`${API_URL}/actors/${actorId}/history`, { params });
  return response.data;
};

const searchUidRegister = async (query, actorId = null) => {
  const response = await axios.get(`${API_URL}/uid-register/search`, {
    params: { q: query, ...(actorId ? { actor_id: actorId } : {}) },
    timeout: 15000,
  });
  return response.data;
};

const confirmBrisRegisterCapture = async ({ countryCode, registerCode, value, actorId = null }) => {
  const response = await axios.post(`${API_URL}/bris-register/confirm`, {
    country_code: countryCode,
    register_code: registerCode,
    value,
    ...(actorId ? { actor_id: actorId } : {}),
    confirmed_on_official_portal: true,
  }, { timeout: 10000 });
  return response.data;
};

const getActorTypesRaw = async () => {
  const response = await axios.get(`${API_URL}/actor-types`);
  return response.data;
};

const checkEmailAvailabilityRaw = async (email, actorId = null) => {
  const response = await axios.get(`${API_URL}/actors/check-email/${encodeURIComponent(email)}`, {
    params: actorId ? { actor_id: actorId } : undefined,
  });
  return {
    exists: response.data.exists,
    message: response.data.message,
  };
};

const getActorsByIdsRaw = async (actorIds) => {
  const actorIdsString = actorIds.join(',');
  const response = await axios.get(`${API_URL}/actors`, {
    params: {
      actor_ids: actorIdsString,
      page: 1,
      limit: 100,
    },
  });
  return response.data;
};

/**
 * Fetch all actors that have a specific tag (e.g. 'driver').
 *
 * Strategy (more reliable than fetch-all-and-filter):
 *   1. Resolve the actor entity type ID from /entity-types.
 *   2. Resolve the tag ID by exact name match from /tags.
 *   3. Ask the backend which entity IDs have that tag via
 *      /entity-tags/{entityType}/{tagId}/entities — backend filters, not us.
 *   4. Fetch ONLY those actors via /actors?actor_ids=...
 *
 * This guarantees we only get actors that genuinely have the tag, and avoids
 * edge cases where batch-tags returns inconsistent or partial data.
 */
const getActorsByTagRaw = async (tagName) => {
  try {
    const TAG_API_URL = isDevelopment
      ? 'http://localhost:8082'
      : 'https://erp.escooterlab.ch/tag-management';
    const TAG_API_BASE = `${TAG_API_URL}/api/v1/tag-management`;

    // ─── 1. Resolve actor entity type ID ────────────────────────────────
    let actorEntityTypeId = null;
    let actorEntityTypeSlug = 'actor'; // fallback slug used by some endpoints
    try {
      const entityTypesResponse = await axios.get(`${TAG_API_BASE}/entity-types`);
      const entityTypes = Array.isArray(entityTypesResponse.data)
        ? entityTypesResponse.data
        : entityTypesResponse.data?.entity_types || [];

      const actorEntityType = entityTypes.find(
        (et) => (et.name || '').toLowerCase() === 'actor'
      );
      if (!actorEntityType) {
        console.warn(
          `[ActorAPI:getActorsByTag] Entity type "actor" not found. Available:`,
          entityTypes.map((et) => et.name)
        );
        return [];
      }
      actorEntityTypeId = actorEntityType.id;
      actorEntityTypeSlug = (actorEntityType.name || 'actor').toLowerCase();
      console.log(
        `[ActorAPI:getActorsByTag] entity type 'actor' -> id=${actorEntityTypeId}`
      );
    } catch (e) {
      console.error('[ActorAPI:getActorsByTag] Failed to fetch entity types:', e);
      return [];
    }

    // ─── 2. Resolve tag ID by exact name match ──────────────────────────
    // Try scoped to the actor entity type first; fall back to an unscoped
    // search since a tag may not be explicitly associated with the actor
    // entity type but can still be applied to actor entities.
    let tagId = null;
    const findExactTag = (tagsArray) =>
      (tagsArray || []).find(
        (t) => (t.name || '').toLowerCase() === tagName.toLowerCase()
      );

    try {
      // Attempt 1: scoped to actor entity type
      const scopedResp = await axios.get(`${TAG_API_BASE}/tags`, {
        params: {
          page: 1,
          limit: 100,
          search: tagName,
          entity_type_id: actorEntityTypeId,
        },
      });
      let exactTag = findExactTag(scopedResp.data?.tags);

      // Attempt 2: unscoped fallback
      if (!exactTag) {
        const unscopedResp = await axios.get(`${TAG_API_BASE}/tags`, {
          params: { page: 1, limit: 100, search: tagName },
        });
        exactTag = findExactTag(unscopedResp.data?.tags);
        if (exactTag) {
          console.log(
            `[ActorAPI:getActorsByTag] tag '${tagName}' found via unscoped lookup (not associated with actor entity type, but still usable).`
          );
        }
      }

      if (!exactTag) {
        console.warn(
          `[ActorAPI:getActorsByTag] Tag "${tagName}" not found in the tag service.`
        );
        return [];
      }
      tagId = exactTag.id;
      console.log(
        `[ActorAPI:getActorsByTag] tag '${tagName}' -> id=${tagId}`
      );
    } catch (e) {
      console.error('[ActorAPI:getActorsByTag] Failed to fetch tags:', e);
      return [];
    }

    // ─── 3. Ask backend which entity IDs have this tag ──────────────────
    let taggedEntityIds = [];
    try {
      const url = `${TAG_API_BASE}/entity-tags/${actorEntityTypeSlug}/${tagId}/entities`;
      const entitiesResponse = await axios.get(url);
      // Backend may return either { entity_ids: [...] } or an array of objects
      const data = entitiesResponse.data;
      if (Array.isArray(data)) {
        taggedEntityIds = data
          .map((row) => row.entity_id || row.id || row)
          .filter(Boolean);
      } else if (Array.isArray(data?.entity_ids)) {
        taggedEntityIds = data.entity_ids;
      } else if (Array.isArray(data?.entities)) {
        taggedEntityIds = data.entities
          .map((e) => e.entity_id || e.id)
          .filter(Boolean);
      }
      taggedEntityIds = taggedEntityIds.map(String);
      console.log(
        `[ActorAPI:getActorsByTag] backend reports ${taggedEntityIds.length} actor(s) with tag '${tagName}':`,
        taggedEntityIds
      );
    } catch (e) {
      console.error(
        `[ActorAPI:getActorsByTag] Failed to fetch entities for tag '${tagName}':`,
        e
      );
      return [];
    }

    if (taggedEntityIds.length === 0) {
      console.log(`[ActorAPI:getActorsByTag] No actors carry the '${tagName}' tag.`);
      return [];
    }

    // ─── 4. Fetch only those actors ─────────────────────────────────────
    try {
      const actorsResult = await getActorsByIdsRaw(taggedEntityIds);
      const fetched = actorsResult?.actors || [];

      // Defensive: keep only the actors whose IDs were in the tag list
      // (in case the backend ignores actor_ids and returns everything).
      const idSet = new Set(taggedEntityIds);
      const finalActors = fetched.filter((a) => idSet.has(String(a.id)));

      console.log(
        `[ActorAPI:getActorsByTag] returning ${finalActors.length} actor(s) for tag '${tagName}'.`
      );
      return finalActors;
    } catch (e) {
      console.error('[ActorAPI:getActorsByTag] Failed to fetch actors by IDs:', e);
      return [];
    }
  } catch (error) {
    console.error('[ActorAPI:getActorsByTag] Unexpected error:', error);
    return [];
  }
};

// ===================
// Cached methods
// ===================

const getActors = withCache(getActorsRaw, { ttl: 30000, id: 'getActors' });
const getActor = withCache(getActorRaw, { ttl: 30000, id: 'getActor' });
const getActorTypes = withCache(getActorTypesRaw, { ttl: 3000, id: 'getActorTypes' });
const checkEmailAvailability = withCache(checkEmailAvailabilityRaw, { ttl: 2000, id: 'checkEmailAvailability' });
const getActorsByIds = withCache(getActorsByIdsRaw, { ttl: 30000, id: 'getActorsByIds' });
const getActorsByTag = withCache(getActorsByTagRaw, { ttl: 5000, id: 'getActorsByTag' });

// ===================
// Main API Object
// ===================

const actorApi = {
  getActors,
  getActor,
  getActorTypes,
  checkEmailAvailability,
  getActorsByIds,
  getActorsByTag,
  getActorHistory,
  searchUidRegister,
  confirmBrisRegisterCapture,

  redactActorHistory: async (actorId, fields = []) => {
    const response = await axios.post(`${API_URL}/actors/${actorId}/history/redact`, { fields });
    return response.data;
  },

  getSalesContactAccess: async (actorId) => {
    const response = await axios.get(`${API_URL}/sales/staff/${actorId}/contact-access`);
    return response.data;
  },

  saveSalesContactAccess: async (actorId, access) => {
    const response = await axios.put(`${API_URL}/sales/staff/${actorId}/contact-access`, access, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (typeof getActor.clearCache === 'function') getActor.clearCache();
    return response.data;
  },

  getClientContextEvents: async (actorId, limit = 100) => {
    const response = await axios.get(`${API_URL}/actors/${actorId}/client-context-events`, {
      params: { limit },
    });
    return response.data;
  },

  createActor: async (actorData) => {
    const response = await axios.post(`${API_URL}/actors`, actorData, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  updateActor: async (actorId, actorData) => {
    const response = await axios.put(`${API_URL}/actors/${actorId}`, actorData, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (typeof getActor.clearCache === 'function') getActor.clearCache();
    if (typeof getActors.clearCache === 'function') getActors.clearCache();
    if (typeof getActorsByIds.clearCache === 'function') getActorsByIds.clearCache();
    return response.data;
  },

  // Contacts are archived, never removed: conversations, repairs and invoices
  // keep pointing at them. `deleteActor` is kept as the archive call for callers.
  deleteActor: async (id, reason) => {
    const response = await axios.post(`${API_URL}/actors/${id}/archive`, reason ? { reason } : {});
    if (typeof getActor.clearCache === 'function') getActor.clearCache();
    if (typeof getActors.clearCache === 'function') getActors.clearCache();
    if (typeof getActorsByIds.clearCache === 'function') getActorsByIds.clearCache();
    return response.data;
  },

  archiveActor: async (id, reason) => actorApi.deleteActor(id, reason),

  // Removes a contact for good. The service refuses with 409 and names what
  // still points at it, so the caller can offer archiving instead.
  deleteActorPermanently: async (id) => {
    const response = await axios.delete(`${API_URL}/actors/${id}/permanent`);
    if (typeof getActor.clearCache === 'function') getActor.clearCache();
    if (typeof getActors.clearCache === 'function') getActors.clearCache();
    if (typeof getActorsByIds.clearCache === 'function') getActorsByIds.clearCache();
    return response.data;
  },

  // Extra phone numbers and e-mail addresses of one contact.
  addContactPoint: async (actorId, { kind, value, label }) => {
    const response = await axios.post(`${API_URL}/actors/${actorId}/contact-points`, { kind, value, label, source: 'erp' });
    if (typeof getActor.clearCache === 'function') getActor.clearCache();
    return response.data;
  },

  deleteContactPoint: async (actorId, pointId) => {
    const response = await axios.delete(`${API_URL}/actors/${actorId}/contact-points/${pointId}`);
    if (typeof getActor.clearCache === 'function') getActor.clearCache();
    return response.data;
  },

  restoreActor: async (id) => {
    const response = await axios.post(`${API_URL}/actors/${id}/restore`);
    if (typeof getActor.clearCache === 'function') getActor.clearCache();
    if (typeof getActors.clearCache === 'function') getActors.clearCache();
    if (typeof getActorsByIds.clearCache === 'function') getActorsByIds.clearCache();
    return response.data;
  },

  addAddress: async (actorId, addressData) => {
    const response = await axios.post(`${API_URL}/actors/${actorId}/addresses`, addressData, {
      headers: { 'Content-Type': 'application/json' },
    });
    // Clear getActor cache to ensure fresh data on next fetch
    getActor.clearCache();
    return response.data;
  },

  updateAddress: async (actorId, addressId, addressData) => {
    const response = await axios.put(`${API_URL}/actors/${actorId}/addresses/${addressId}`, addressData, {
      headers: { 'Content-Type': 'application/json' },
    });
    // Clear getActor cache to ensure fresh data on next fetch
    getActor.clearCache();
    return response.data;
  },

  deleteAddress: async (actorId, addressId) => {
    const response = await axios.delete(`${API_URL}/actors/${actorId}/addresses/${addressId}`);
    // Clear getActor cache to ensure fresh data on next fetch
    getActor.clearCache();
    return response.data;
  },

  uploadProfilePicture: async (actorId, file) => {
    if (!actorId) throw new Error('Actor ID is required');

    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      `${API_URL}/actors/${actorId}/profile-picture`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    if (response.data?.profile_picture_url) {
      return response.data;
    }

    throw new Error('Invalid response format from server');
  },

  deleteProfilePicture: async (actorId) => {
    if (!actorId) throw new Error('Actor ID is required');

    const response = await axios.delete(`${API_URL}/actors/${actorId}/profile-picture`);
    return response.data;
  },

  /**
   * Get customers (actors of type 'individual' or 'company')
   * Used for invoice customer selection
   * @param {Object} params - Query parameters
   * @returns {Promise} - Promise with customers list
   */
  getCustomers: async (params = {}) => {
    try {
      const response = await axios.get(`${API_URL}/actors`, {
        params: {
          page: params.page || 1,
          limit: params.limit || 100,
          ...params
        }
      });
      return {
        items: response.data.actors || [],
        total: response.data.total || 0,
        total_pages: response.data.total_pages || 1
      };
    } catch (error) {
      console.error('[Actor API] Error fetching customers:', error);
      throw error;
    }
  },
  getAwardDefinitions: async (includeInactive = false) => {
    const response = await axios.get(`${API_URL}/awards`, { params: { include_inactive: includeInactive } });
    return response.data?.items || [];
  },
  createAwardDefinition: async (definition) => {
    const response = await axios.post(`${API_URL}/awards`, definition);
    return response.data;
  },
  updateAwardDefinition: async (id, definition) => {
    const response = await axios.put(`${API_URL}/awards/${id}`, definition);
    return response.data;
  },
  // Badges and certificates for a whole page of actors, one call each, so a
  // list can show them without a request per row.
  getAwardAssignmentsByActors: async (actorIds) => {
    if (!actorIds || actorIds.length === 0) return {};
    const response = await axios.get(`${API_URL}/award-assignments`, {
      params: { actor_ids: actorIds.join(',') },
    });
    return response.data || {};
  },
  getCertificatesByActors: async (actorIds) => {
    if (!actorIds || actorIds.length === 0) return {};
    const response = await axios.get(`${API_URL}/actor-certificates`, {
      params: { actor_ids: actorIds.join(',') },
    });
    return response.data || {};
  },

  // Certificates the actor holds themselves, next to the ones they hold as a
  // mechanic at a workshop (ADR-0044).
  getActorCertificates: async (actorId, history = false) => {
    const response = await axios.get(`${API_URL}/actors/${actorId}/certificates`, { params: { history } });
    return response.data;
  },
  createActorCertificate: async (actorId, certificate) => {
    const response = await axios.post(`${API_URL}/actors/${actorId}/certificates`, certificate);
    return response.data;
  },
  updateActorCertificate: async (actorId, certificateId, certificate) => {
    const response = await axios.put(`${API_URL}/actors/${actorId}/certificates/${certificateId}`, certificate);
    return response.data;
  },
  revokeActorCertificate: async (actorId, certificateId, reason) => {
    const response = await axios.post(`${API_URL}/actors/${actorId}/certificates/${certificateId}/revoke`, { reason });
    return response.data;
  },
  getActorAwards: async (actorId, history = false, language) => {
    const response = await axios.get(`${API_URL}/actors/${actorId}/awards`, {
      params: { history, ...(language ? { language } : {}) },
    });
    return response.data?.items || [];
  },
  /**
   * The badge catalogue for one kind of recipient, in one language.
   *
   * A person's page has no use for badges only a company may hold, and the
   * server decides that rather than the screen guessing from the name.
   */
  getAwardsFor: async ({ recipientType, language, includeInactive = false } = {}) => {
    const response = await axios.get(`${API_URL}/awards`, {
      params: {
        include_inactive: includeInactive,
        ...(recipientType ? { recipient_type: recipientType } : {}),
        ...(language ? { language } : {}),
      },
    });
    return response.data;
  },

  /**
   * Who holds each badge, for the whole catalogue in one call.
   *
   * The catalogue shows a count per badge and the people behind the selected
   * one; neither may become a request per row.
   */
  getAwardHolders: async (awardIds) => {
    const response = await axios.get(`${API_URL}/award-holders`, {
      params: awardIds && awardIds.length ? { award_ids: awardIds.join(',') } : {},
    });
    return response.data;
  },

  /** How a meeting presents recognition - a company decision, one row. */
  getMeetingDisplaySettings: async () => {
    const response = await axios.get(`${API_URL}/award-meeting-settings`);
    return response.data;
  },
  setMeetingDisplaySettings: async (settings) => {
    const response = await axios.put(`${API_URL}/award-meeting-settings`, settings);
    return response.data;
  },

  /** The participants a meeting would show, with the badges they chose. */
  getMeetingDisplay: async ({ limit = 8, language } = {}) => {
    const response = await axios.get(`${API_URL}/award-meeting-display`, {
      params: { limit, ...(language ? { language } : {}) },
    });
    return response.data;
  },

  /** Which badges this actor shows in a meeting, in the order given (at most three). */
  setMeetingSelection: async (actorId, assignmentIds) => {
    const response = await axios.put(`${API_URL}/actors/${actorId}/awards/meeting-selection`,
      { assignment_ids: assignmentIds });
    return response.data;
  },

  grantActorAward: async (actorId, awardDefinitionId, reason) => {
    const response = await axios.post(`${API_URL}/actors/${actorId}/awards`, {
      award_definition_id: awardDefinitionId,
      reason,
    });
    return response.data;
  },
  revokeActorAward: async (actorId, assignmentId, reason) => {
    const response = await axios.post(`${API_URL}/actors/${actorId}/awards/${assignmentId}/revoke`, { reason });
    return response.data;
  },
  /**
   * Get the profile picture URL for an actor
   * @param {string} actorId - The actor ID
   * @param {number} timestamp - Optional cache-busting timestamp
   * @returns {string} - The full profile picture URL
   */
  getProfilePictureUrl: (actorId, timestamp = null) => {
    const ts = timestamp || Date.now();
    // Dev: http://localhost:8081/api/v1/actor-management/profile-picture
    // Prod: https://erp.escooterlab.ch/actor-management/api/v1/profile-picture
    // In dev, we need /actor-management in path. In prod, it's already in BASE_URL.
    const path = isDevelopment 
      ? '/api/v1/actor-management/profile-picture'
      : '/api/v1/profile-picture';
    return `${BASE_URL}${path}?actor_id=${actorId}&t=${ts}`;
  },

  /**
   * Get the base API URL (for components that need direct URL access)
   * @returns {string} - The base API URL
   */
  getApiUrl: () => API_URL,
};

export default actorApi;
